import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  Box,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { FiSend } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';

/**
 * Reusable modal for sending a test SMS to a custom phone number.
 * Expects the caller to pass a fully resolved message (variables already replaced).
 * Sends via the existing sendDialpadSMS Cloud Function without contact/deal IDs
 * so no activity logs are created for the test.
 */
export default function SendTestSMSModal({ isOpen, onClose, message, mediaUrl = null }) {
  const [testPhone, setTestPhone] = useState('');
  const [isSending, setIsSending] = useState(false);
  const toast = useToast();

  const handleSend = async () => {
    if (!testPhone.trim()) {
      toast({ title: 'Phone number is required', status: 'warning', duration: 2000 });
      return;
    }

    if (!message?.trim() && !mediaUrl) {
      toast({ title: 'No message or image to send', status: 'warning', duration: 2000 });
      return;
    }

    setIsSending(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const sendSMS = httpsCallable(functions, 'sendDialpadSMS');

      const result = await sendSMS({
        phoneNumber: testPhone.trim(),
        message: (message || '').trim(),
        mediaUrl: mediaUrl || null,
      });

      if (result.data.success) {
        toast({
          title: 'Test SMS sent!',
          description: `Message sent to ${testPhone}`,
          status: 'success',
          duration: 4000,
        });
        setTestPhone('');
        onClose();
      }
    } catch (error) {
      console.error('Error sending test SMS:', error);

      let errorMessage = error.message;
      if (error.message?.includes('not enabled') || error.message?.includes('failed-precondition')) {
        errorMessage = 'Dialpad is not enabled for your account. Please enable it in CRM Settings.';
      } else if (error.message?.includes('Rate limit') || error.message?.includes('resource-exhausted')) {
        errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
      } else if (error.message?.includes('Invalid phone number')) {
        errorMessage = error.message;
      }

      toast({
        title: 'Failed to send test SMS',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const charCount = message?.length || 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Send Test SMS</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                This will send a real SMS to the number below using the
                selected contact's data for variable replacement.
              </Text>
            </Alert>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Send test to phone number</FormLabel>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="(555) 555-1234 or +15555551234"
                size="sm"
                type="tel"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Enter the phone number to receive the test message
              </Text>
            </FormControl>

            <Box bg="gray.50" borderRadius="md" p={3} borderWidth="1px" borderColor="gray.200">
              <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500">MESSAGE PREVIEW</Text>
                <HStack spacing={2}>
                  {mediaUrl && <Badge colorScheme="purple">MMS image</Badge>}
                  <Badge colorScheme={charCount > 160 ? 'orange' : 'green'}>{charCount} chars</Badge>
                </HStack>
              </HStack>
              <Text fontSize="sm" whiteSpace="pre-wrap">{message || (mediaUrl ? '(image only)' : '(empty)')}</Text>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSend}
            isLoading={isSending}
            loadingText="Sending..."
            leftIcon={<FiSend />}
            isDisabled={!testPhone.trim() || (!message?.trim() && !mediaUrl)}
          >
            Send Test
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
